import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  comparisonSelectedPositionOptionsAtom,
  unwrappedPlayerComparisonPositionsOptionsAtom,
} from '@/atoms/filters/highLevel/comparisonPositions';
import { selectedPositionOptionsAtom } from '@/atoms/filters/highLevel/positions';
import { playerInfoAtom } from '@/atoms/player/info';
import { playerComparisonInfoAtom } from '@/atoms/queries/player/playerComparisonInfo';
import { unwrappedPlayerRenderedComparisonMinutesPlayedAtom } from '@/atoms/queries/player/playerLeagueAverageRadar';
import { unwrappedPlayerAggsMinutesPlayedAtom } from '@/atoms/queries/player/playerMinsPlayed';
import { playerPositionsOptionsAtom } from '@/atoms/queries/player/playerPositions';
import { Positions } from '@/components/radar/Positions';
import { BioDataItem, BioTableContent } from '@/components/radar/table/RadarBioTable';
import { assertBioRowCount, useFormatMinutesPlayed } from '@/hooks/radar/radarBioUtils';
import { usePlayerCompetitionAverageBioColumn } from '@/hooks/radar/usePlayerCompetitionAverageBioColumn';
import {
  useComparisonPlayerFilterDisplayOptions,
  usePlayerFilterDisplayOptions,
} from '@/hooks/radar/usePlayerFilterDisplayOptions';
import { useFormatDob } from '@/hooks/useFormatDob';
import { useIsCompetitionAverageMode } from '@/hooks/useIsCompetitionAverageMode';
import { useComparisonPlayerGameSummary, usePlayerGameSummary } from '@/hooks/usePlayerGameSummary';
import { useRawPositionOptions } from '@/hooks/useRawPositionOptions';
import { BaseOption } from '@/types/generic';

// The number of bio rows. Must be updated whenever rows are added or removed from the hook.
export const MAX_PLAYER_BIO_ROW_COUNT = 7;

const stripCountryCode = (options: BaseOption<unknown>[]) =>
  options.map(({ optional, ...rest }) => {
    if (!optional) return rest;
    const { countryCode: _countryCode, ...restOptional } = optional;
    return { ...rest, optional: restOptional };
  });

export const usePlayerBioData = () => {
  const { t } = useTranslation(['filters', 'entity', 'playerInfo']);
  const formatDob = useFormatDob();
  const formatMinutesPlayed = useFormatMinutesPlayed();
  const isCompetitionAverageMode = useIsCompetitionAverageMode();

  // Entity info
  const playerInfo = useAtomValue(playerInfoAtom);
  const comparisonPlayerInfo = useAtomValue(playerComparisonInfoAtom);
  const entityName = playerInfo?.displayName ?? '';
  const comparisonEntityName = comparisonPlayerInfo?.displayName ?? '';

  // Minutes — mode-resolved so the comparison column shows league-average minutes in competition-average mode
  const minutesPlayed = useAtomValue(unwrappedPlayerAggsMinutesPlayedAtom);
  const comparisonMinutesPlayed = useAtomValue(unwrappedPlayerRenderedComparisonMinutesPlayedAtom);

  // Competitions / Seasons / Teams
  const { competitionsToDisplay, seasonsToDisplay, teamsToDisplay } = usePlayerFilterDisplayOptions();
  const {
    competitionsToDisplay: comparisonCompetitionsToDisplay,
    seasonsToDisplay: comparisonSeasonsToDisplay,
    teamsToDisplay: comparisonTeamsToDisplay,
  } = useComparisonPlayerFilterDisplayOptions();

  // Games
  const gameSummary = usePlayerGameSummary();
  const comparisonGameSummary = useComparisonPlayerGameSummary();

  // Positions
  const playerPositionsOptions = useAtomValue(playerPositionsOptionsAtom);
  const selectedPositionOptions = useAtomValue(selectedPositionOptionsAtom);
  const comparisonPlayerPositionsOptions = useAtomValue(unwrappedPlayerComparisonPositionsOptionsAtom);
  const comparisonSelectedPositionOptions = useAtomValue(comparisonSelectedPositionOptionsAtom);

  const positionOptionsToUse = selectedPositionOptions.length ? selectedPositionOptions : playerPositionsOptions;
  const comparisonPositionOptionsToUse = comparisonSelectedPositionOptions.length
    ? comparisonSelectedPositionOptions
    : comparisonPlayerPositionsOptions;
  const positionsToDisplay = useRawPositionOptions(positionOptionsToUse);
  const comparisonPositionsToDisplay = useRawPositionOptions(comparisonPositionOptionsToUse);

  // Bio values
  const minutesValue = formatMinutesPlayed(minutesPlayed);
  const comparisonMinutesValue = formatMinutesPlayed(comparisonMinutesPlayed);

  const positionsValue = <Positions options={positionsToDisplay} allOptionsCount={playerPositionsOptions.length} />;
  const comparisonPositionsValue = (
    <Positions options={comparisonPositionsToDisplay} allOptionsCount={comparisonPlayerPositionsOptions.length} />
  );
  const dobValue = formatDob(playerInfo) ?? '-';
  const comparisonDobValue = formatDob(comparisonPlayerInfo) ?? '-';

  // The comparison column diverges by mode: an entity comparison shows the other player's scope, while
  // a competition average shows the selected competition/season/template positions.
  const competitionAverageColumn = usePlayerCompetitionAverageBioColumn();
  const entityComparisonColumn = {
    name: comparisonEntityName,
    competition: <BioTableContent options={comparisonCompetitionsToDisplay} />,
    season: <BioTableContent options={comparisonSeasonsToDisplay} />,
    team:
      comparisonTeamsToDisplay.length > 0 ? (
        <BioTableContent options={stripCountryCode(comparisonTeamsToDisplay)} />
      ) : (
        '-'
      ),
    game: comparisonGameSummary,
    position: comparisonPositionsValue,
    dob: comparisonDobValue,
  };
  const comparison = isCompetitionAverageMode ? competitionAverageColumn : entityComparisonColumn;

  const bioData: BioDataItem[] = [
    {
      label: t('summary.minutesPlayed', { ns: 'filters' }),
      value: minutesValue,
      comparisonValue: comparisonMinutesValue,
    },
    {
      label: t('competition_one', { ns: 'entity' }),
      value: <BioTableContent options={competitionsToDisplay} />,
      comparisonValue: comparison.competition,
    },
    {
      label: t('season_one', { ns: 'entity' }),
      value: <BioTableContent options={seasonsToDisplay} />,
      comparisonValue: comparison.season,
    },
    {
      label: t('team_one', { ns: 'entity' }),
      value: teamsToDisplay.length > 0 ? <BioTableContent options={stripCountryCode(teamsToDisplay)} /> : '-',
      comparisonValue: comparison.team,
    },
    {
      label: t('game_one', { ns: 'entity' }),
      value: gameSummary,
      comparisonValue: comparison.game,
    },
    {
      label: t('position', { ns: 'filters' }),
      value: positionsValue,
      comparisonValue: comparison.position,
    },
    {
      label: t('dateOfBirth', { ns: 'playerInfo' }),
      value: dobValue,
      comparisonValue: comparison.dob,
    },
  ];

  assertBioRowCount(bioData, MAX_PLAYER_BIO_ROW_COUNT);

  return { bioData, entityName, comparisonEntityName: comparison.name };
};
